"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Sparkles, Stethoscope, Users, Microscope, ShieldCheck, FileCheck, Award, Building2, Target } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GlobalMedicalInnovationPartner() {
  const [lang, setLang] = useState<string>("en");
  useEffect(() => {
    const detectLanguage = () => {
      const htmlLang = document.documentElement.lang?.toLowerCase() || "";
      const bodyClass = document.body.className.toLowerCase();
      if (htmlLang.startsWith("zh") || bodyClass.includes("translatepress-zh_tw") || bodyClass.includes("translatepress-zh-tw")) {
        setLang("zh");
      } else if (htmlLang.startsWith("de")) {
        setLang("de");
      } else if (htmlLang.startsWith("ja")) {
        setLang("ja");
      } else if (htmlLang.startsWith("es")) {
        setLang("es");
      } else {
        setLang("en");
      }
    };
    detectLanguage();
    const observer = new MutationObserver(detectLanguage);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "class"]
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"]
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  const translations: Record<string, any> = {
    en: {
      badge: "Your Trusted Clinical Partner for Global Medical Innovation",
      headingPart1: "Global Medical Device Distributor & Strategic Partner",
      description: "MS Biotechnology serves as your premier global conduit for clinical excellence, bridging the gap between high-tech medical innovation and international healthcare markets. We specialize in the meticulous identification, rigorous clinical evaluation, and strategic distribution of high-impact medical solutions. By curating a portfolio of advanced technologies from leading global manufacturers, we empower healthcare providers and distributors to access cutting-edge advancements with confidence. Our commitment ensures that every solution we deliver meets the highest standards of clinical efficacy, regulatory compliance, and operational efficiency worldwide.",
      ctaExplore: "Explore Solutions",
      ctaMission: "Our Story"
    },
    zh: {
      badge: "Your Trusted Clinical Partner for Global Medical Innovation",
      headingPart1: "全球医疗器械分销商与战略合作伙伴",
      description: "MS Biotechnology serves as your premier global conduit for clinical excellence, bridging the gap between high-tech medical innovation and international healthcare markets. We specialize in the meticulous identification, rigorous clinical evaluation, and strategic distribution of high-impact medical solutions. By curating a portfolio of advanced technologies from leading global manufacturers, we empower healthcare providers and distributors to access cutting-edge advancements with confidence. Our commitment ensures that every solution we deliver meets the highest standards of clinical efficacy, regulatory compliance, and operational efficiency worldwide.",
      ctaExplore: "Explore Solutions",
      ctaMission: "Our Story"
    },
    de: {
      badge: "Your Trusted Clinical Partner for Global Medical Innovation",
      headingPart1: "Globaler Vertriebspartner für Medizinprodukte & Strategischer Partner",
      description: "MS Biotechnology serves as your premier global conduit for clinical excellence, bridging the gap between high-tech medical innovation and international healthcare markets. We specialize in the meticulous identification, rigorous clinical evaluation, and strategic distribution of high-impact medical solutions. By curating a portfolio of advanced technologies from leading global manufacturers, we empower healthcare providers and distributors to access cutting-edge advancements with confidence. Our commitment ensures that every solution we deliver meets the highest standards of clinical efficacy, regulatory compliance, and operational efficiency worldwide.",
      ctaExplore: "Explore Solutions",
      ctaMission: "Our Story"
    },
    ja: {
      badge: "Your Trusted Clinical Partner for Global Medical Innovation",
      headingPart1: "グローバル医療機器販売代理店および戦略的パートナー",
      description: "MS Biotechnology serves as your premier global conduit for clinical excellence, bridging the gap between high-tech medical innovation and international healthcare markets. We specialize in the meticulous identification, rigorous clinical evaluation, and strategic distribution of high-impact medical solutions. By curating a portfolio of advanced technologies from leading global manufacturers, we empower healthcare providers and distributors to access cutting-edge advancements with confidence. Our commitment ensures that every solution we deliver meets the highest standards of clinical efficacy, regulatory compliance, and operational efficiency worldwide.",
      ctaExplore: "Explore Solutions",
      ctaMission: "Our Story"
    },
    es: {
      badge: "Your Trusted Clinical Partner for Global Medical Innovation",
      headingPart1: "Distribuidor Global de Dispositivos Médicos y Socio Estratégico",
      description: "MS Biotechnology serves as your premier global conduit for clinical excellence, bridging the gap between high-tech medical innovation and international healthcare markets. We specialize in the meticulous identification, rigorous clinical evaluation, and strategic distribution of high-impact medical solutions. By curating a portfolio of advanced technologies from leading global manufacturers, we empower healthcare providers and distributors to access cutting-edge advancements with confidence. Our commitment ensures that every solution we deliver meets the highest standards of clinical efficacy, regulatory compliance, and operational efficiency worldwide.",
      ctaExplore: "Explore Solutions",
      ctaMission: "Our Story"
    }
  };
  const t = translations[lang] || translations["en"];
  const heroVideo = lang === "zh" ? "https://ms-biotechnology.com/wp-content/uploads/2026/07/msbio-hero-zh.mp4.mp4" : "https://ms-biotechnology.com/wp-content/uploads/2026/07/msbio-hero-en.mp4.mp4";
  console.log("heroVideo =", heroVideo);
  const {
    ref: sectionRef
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const cards = [{
    icon: Stethoscope,
    text: "About Our Story",
    url: "/AboutMSBiotechnologyPage",
    link: {
      to: "/AboutMSBiotechnologyPage"
    }
  }, {
    icon: Users,
    text: "How We Support Partners",
    url: "/SolutionsPage",
    link: {
      to: "/OurProcessPage"
    }
  }, {
    icon: Microscope,
    text: "Medical Portfolio",
    url: "/TechnologyPortfolioPage",
    link: {
      to: "/TechnologyPortfolioPage"
    }
  }, {
    icon: ShieldCheck,
    text: "Why Choose MS",
    url: "/WhyPartnerWithMSPage",
    link: {
      to: "/WhyPartnerWithMSPage"
    }
  }, {
    icon: FileCheck,
    text: "Quality & Market Readiness",
    url: "/CompliancePage",
    link: {
      to: "/CompliancePage"
    }
  }];
  return <section data-section-id="118" id="global-medical-innovation-partner" ref={sectionRef} className="relative overflow-hidden bg-white py-32 lg:py-48">
      <div className="absolute top-40 left-4 hidden lg:block">
        
      </div>

      <div aria-hidden="true" className="pointer-events-none absolute -top-20 -right-20 h-[40rem] w-[40rem] rounded-lg blur-3xl opacity-20" style={{
      background: "radial-gradient(circle, hsl(174 75% 38% / 0.4) 0%, transparent 70%)"
    }} />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-9 flex flex-col gap-12">
            <div className="flex flex-col gap-8 text-left">
              <Badge variant="secondary" className="bg-[hsl(174_75%_38%_/_0.15)] text-[hsl(174_75%_38%)] border-none rounded-lg px-4 py-1.5 text-xs font-medium tracking-tight uppercase w-fit">
                {t.badge}
              </Badge>

             <div className="relative w-full h-[520px] md:h-[620px] overflow-hidden rounded-3xl shadow-xl bg-slate-100">
  <video key={heroVideo} className="absolute inset-0 w-full h-full object-cover" controls playsInline preload="auto">
    <source src={heroVideo} type="video/mp4" />
  </video>
</div>

<div className="flex flex-col gap-3">
  <h1 className="text-6xl font-default font-bold text-slate-900 leading-[1.1]">
    {t.headingPart1}
  </h1>
</div>

              <p className="text-slate-600 text-lg md:text-xl font-default leading-relaxed text-left max-w-[800px]">
                {t.description}
              </p>

              
            </div>

            <div className="relative h-px w-full bg-slate-200">
              <div className="absolute -top-[2px] left-0 h-1 w-1 bg-[hsl(174_75%_38%)]" />
            </div>

            <div className="flex flex-wrap items-center gap-6">
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }}>
                <Link to="/TechnologyPortfolioPage">
                  
                </Link>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }}>
                <Link to="/AboutMSBiotechnologyPage">
                  
                </Link>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-12">
              {cards.map((card, i) => <div key={i} data-index={i} className="bg-white/70 backdrop-blur-md border border-white/20 rounded-2xl shadow-md flex flex-col gap-4 !items-center !justify-start ![padding:24px_20px_24px_20px] !w-[130px]">
                  <card.icon className="size-8 text-[hsl(174_75%_38%)]" />
                  <Link className="text-sm font-medium text-slate-900 font-default leading-relaxed text-muted-foreground !text-center underline cursor-pointer" {...card.link}><p>{card.text}</p></Link>
                  <Link className="text-xs font-semibold text-[hsl(174_75%_38%)] hover:underline underline cursor-pointer" {...card.link}></Link>
                </div>)}
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        .vertical-rl {
          writing-mode: vertical-rl;
        }
      `}</style>
    </section>;
}